<script setup>
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import GoogleCalendarConnection from 'dashboard/routes/dashboard/crm/calendar/components/GoogleCalendarConnection.vue';
import { useModulePermissions } from 'dashboard/composables/useModulePermissions';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import AModal from './AModal.vue';
import WorkingHoursEditor from './WorkingHoursEditor.vue';
import { ICON } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;
// Gating de ações por FUNÇÃO: esconde criar/editar/excluir conforme a permissão
// CRUD do módulo appointments (o backend recusa de todo jeito; o botão some).
const { moduleCan } = useModulePermissions();
const canCreateAppointments = computed(() =>
  moduleCan('create', 'appointments')
);
const canEditAppointments = computed(() => moduleCan('update', 'appointments'));
const canDeleteAppointments = computed(() =>
  moduleCan('delete', 'appointments')
);

// Google Agenda do profissional (spec 010): so administrador e com a conta liberada no Super Admin.
const route = useRoute();
const { isAdmin } = useAdmin();
const getAccount = useMapGetter('accounts/getAccount');
const showGoogleCalendar = computed(
  () =>
    isAdmin.value &&
    getAccount.value(props.accountId)?.settings?.google_calendar_enabled ===
      true
);

const practitioners = ref([]);
const services = ref([]);
const show = ref(false);
const form = ref({});
const workingHours = ref([]);
const lunch = ref(false);
const sel = ref([]);

const load = async () => {
  practitioners.value =
    (
      await axios.get(`${base}/practitioners`, {
        params: { kind: 'practitioner' },
      })
    ).data.data || [];
  services.value =
    (await axios.get(`${base}/appointment_services`)).data.data || [];
};
// Link do calendario: so existe depois de salvo; vem do detalhe do profissional.
const icsUrl = ref('');
const carregarLink = async id => {
  icsUrl.value =
    (await axios.get(`${base}/practitioners/${id}`)).data.data?.ics_url || '';
};
const copiarLink = async () => {
  await copyTextToClipboard(icsUrl.value);
  useAlert(t('ATENDIMENTOS.practitioners.modal.icsCopied'));
};
const trocarLink = async () => {
  if (!window.confirm(t('ATENDIMENTOS.practitioners.modal.icsRotateConfirm')))
    return;
  const { data } = await axios.patch(
    `${base}/practitioners/${form.value.id}/rotate_ics`
  );
  icsUrl.value = data.data?.ics_url || '';
};

const open = (p = null) => {
  icsUrl.value = '';
  if (p) {
    form.value = { ...p };
    carregarLink(p.id);
    sel.value = [...(p.service_ids || [])];
    lunch.value = !!(p.lunch_start && p.lunch_end);
    workingHours.value = (p.working_hours || []).map(w => ({
      day: w.day,
      startTime: w.startTime,
      endTime: w.endTime,
    }));
  } else {
    form.value = {
      name: '',
      specialty: '',
      registration_no: '',
      email: '',
      phone: '',
      color: '#6366F1',
      buffer_minutes: 0,
      capacity: 1,
      lunch_start: '12:00',
      lunch_end: '13:00',
      is_active: true,
    };
    sel.value = [];
    lunch.value = false;
    workingHours.value = [];
  }
  show.value = true;
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  const payload = {
    ...form.value,
    working_hours: JSON.stringify(workingHours.value),
    service_ids: sel.value,
  };
  if (!lunch.value) {
    payload.lunch_start = null;
    payload.lunch_end = null;
  }
  if (form.value.id)
    await axios.patch(`${base}/practitioners/${form.value.id}`, payload);
  else await axios.post(`${base}/practitioners`, payload);
  show.value = false;
  await load();
};
const remove = async p => {
  if (
    !window.confirm(
      t('ATENDIMENTOS.practitioners.deleteConfirm', { name: p.name })
    )
  )
    return;
  try {
    await axios.delete(`${base}/practitioners/${p.id}`);
  } catch (error) {
    useAlert(
      error.response?.status === 422
        ? error.response.data.error
        : t('ATENDIMENTOS.practitioners.deleteError')
    );
    return;
  }
  await load();
};
const toggleActive = async p => {
  const key = p.is_active ? 'inactivateConfirm' : 'reactivateConfirm';
  if (!window.confirm(t(`ATENDIMENTOS.practitioners.${key}`, { name: p.name })))
    return;
  await axios.patch(`${base}/practitioners/${p.id}`, {
    is_active: !p.is_active,
  });
  await load();
};
const toggleSvc = id => {
  const i = sel.value.indexOf(id);
  if (i >= 0) sel.value.splice(i, 1);
  else sel.value.push(id);
};
const svcName = id => services.value.find(s => s.id === id)?.name;

// Volta do Google: reabre o profissional que estava sendo conectado.
const openFromQuery = () => {
  const id = Number(route.query.practitioner_id);
  const practitioner = id && practitioners.value.find(p => p.id === id);
  if (practitioner) open(practitioner);
};

onMounted(async () => {
  await load();
  openFromQuery();
});
</script>

<template>
  <div class="p-6">
    <div class="flex justify-end mb-4">
      <button
        v-if="canCreateAppointments"
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.practitioners.newPractitioner') }}
      </button>
    </div>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="p in practitioners"
        :key="p.id"
        class="p-4 bg-n-solid-2 border rounded-xl border-n-weak"
        :class="{ 'opacity-60': !p.is_active }"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-3">
            <span
              class="flex items-center justify-center w-10 h-10 text-sm font-semibold text-white rounded-full"
              :style="{ background: p.color }"
              >{{ (p.name || '?')[0] }}</span
            >
            <div>
              <div class="font-semibold text-n-slate-12">
                {{ p.name }}
                <span
                  v-if="!p.is_active"
                  class="ms-1 px-2 py-0.5 text-xs font-normal rounded-full bg-n-slate-3 text-n-slate-11"
                  >{{ t('ATENDIMENTOS.practitioners.inactiveBadge') }}</span
                >
              </div>
              <div class="text-sm text-n-slate-10">
                {{ p.specialty || '—' }}
              </div>
            </div>
          </div>
          <button
            v-if="canEditAppointments"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="open(p)"
          >
            {{ t('ATENDIMENTOS.practitioners.edit') }}
          </button>
        </div>
        <div
          v-if="p.lunch_start"
          class="inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800"
        >
          <svg
            class="w-3 h-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" :d="ICON.clock[0]" />
          </svg>
          {{ p.lunch_start }}–{{ p.lunch_end }}
        </div>
        <div class="flex flex-wrap gap-1.5 mt-3">
          <span
            v-for="sid in p.service_ids || []"
            :key="sid"
            class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-1 text-n-slate-11"
            >{{ svcName(sid) }}</span
          >
        </div>
        <div class="flex gap-4 mt-3">
          <button
            v-if="canEditAppointments"
            data-test="practitioner-toggle-active"
            class="text-xs text-n-slate-11 hover:underline"
            @click="toggleActive(p)"
          >
            {{
              t(
                p.is_active
                  ? 'ATENDIMENTOS.practitioners.inactivate'
                  : 'ATENDIMENTOS.practitioners.reactivate'
              )
            }}
          </button>
          <button
            v-if="canDeleteAppointments"
            data-test="practitioner-delete"
            class="text-xs text-n-ruby-11 hover:underline"
            @click="remove(p)"
          >
            {{ t('ATENDIMENTOS.practitioners.delete') }}
          </button>
        </div>
      </div>
      <p v-if="!practitioners.length" class="text-n-slate-10">
        {{ t('ATENDIMENTOS.practitioners.empty') }}
      </p>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.practitioners.modal.editTitle')
          : t('ATENDIMENTOS.practitioners.modal.newTitle')
      "
      @close="show = false"
    >
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.namefield')
          }}</label
          ><input
            v-model="form.name"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.color')
          }}</label
          ><input
            v-model="form.color"
            type="color"
            class="w-12 h-9 p-0.5 border rounded border-n-weak bg-n-background"
          />
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.specialty')
          }}</label
          ><input
            v-model="form.specialty"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.registrationNo')
          }}</label
          ><input
            v-model="form.registration_no"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.email')
          }}</label
          ><input
            v-model="form.email"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.phone')
          }}</label
          ><input
            v-model="form.phone"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.availableDays')
        }}</label>
        <WorkingHoursEditor v-model="workingHours" />
      </div>
      <div>
        <label class="flex items-center gap-2 text-sm"
          ><input v-model="lunch" type="checkbox" />
          {{ t('ATENDIMENTOS.practitioners.modal.lunchLabel') }}</label
        >
        <div v-if="lunch" class="flex items-center gap-2 mt-1">
          <input
            v-model="form.lunch_start"
            type="time"
            class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          /><span class="text-n-slate-10">{{
            t('ATENDIMENTOS.practitioners.modal.lunchTo')
          }}</span
          ><input
            v-model="form.lunch_end"
            type="time"
            class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <p v-if="lunch" class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.practitioners.modal.lunchNote') }}
        </p>
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.buffer')
        }}</label
        ><input
          v-model="form.buffer_minutes"
          type="number"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.capacity')
        }}</label
        ><input
          v-model.number="form.capacity"
          type="number"
          min="1"
          max="100"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
        <p class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.practitioners.modal.capacityNote') }}
        </p>
      </div>
      <div v-if="icsUrl">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.icsLabel')
        }}</label>
        <div class="flex items-center gap-2">
          <input
            :value="icsUrl"
            readonly
            class="w-full px-3 py-2 text-xs rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11"
          />
          <button
            class="px-3 py-2 text-xs rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-1"
            @click="copiarLink"
          >
            {{ t('ATENDIMENTOS.practitioners.modal.icsCopy') }}
          </button>
          <button
            v-if="canEditAppointments"
            class="px-3 py-2 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="trocarLink"
          >
            {{ t('ATENDIMENTOS.practitioners.modal.icsRotate') }}
          </button>
        </div>
        <p class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.practitioners.modal.icsNote') }}
        </p>
      </div>
      <div v-if="showGoogleCalendar && form.id" data-test="practitioner-google">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('GOOGLE_CALENDAR.PRACTITIONER_SECTION')
        }}</label>
        <GoogleCalendarConnection :practitioner-id="form.id" />
        <p class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('GOOGLE_CALENDAR.PRACTITIONER_HINT') }}
        </p>
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.enabledServices')
        }}</label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="s in services"
            :key="s.id"
            class="px-2 py-1 text-xs rounded-full border"
            :class="
              sel.includes(s.id)
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-background text-n-slate-11 border-n-weak'
            "
            @click="toggleSvc(s.id)"
          >
            {{ s.name }}
          </button>
        </div>
      </div>
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.cancel') }}</button
        ><button
          v-if="form.id ? canEditAppointments : canCreateAppointments"
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
